import React, { useState, useEffect } from 'react'
import { 
  ShoppingCart, Calendar, Users, Plus, Check, X, 
  Printer, Download, RefreshCw, ChevronDown, ChevronUp,
  Package, Carrot, Fish, Milk, Cookie, Coffee, Droplet,
  CheckCircle, Circle, Trash2, Edit2, Save
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, addDays } from 'date-fns'

interface ShoppingListItem {
  id?: string
  name: string
  quantity: number | string
  unit: string
  section: string
  recipes: string[]
  checked: boolean
}

interface ShoppingListSection {
  section: string
  items: ShoppingListItem[]
}

interface ShoppingList {
  id: string
  date_range: {
    start: string
    end: string
  }
  servings_multiplier: number
  total_items: number
  sections: ShoppingListSection[]
  meals_included?: any[]
}

const sectionIcons: { [key: string]: React.ReactNode } = {
  'Produce': <Carrot className="w-5 h-5" />,
  'Meat': <Package className="w-5 h-5" />,
  'Seafood': <Fish className="w-5 h-5" />,
  'Dairy': <Milk className="w-5 h-5" />,
  'Bakery': <Cookie className="w-5 h-5" />,
  'Pantry': <Package className="w-5 h-5" />,
  'Beverages': <Coffee className="w-5 h-5" />,
  'Condiments': <Droplet className="w-5 h-5" />,
  'Other': <ShoppingCart className="w-5 h-5" />
}

const sectionColors: { [key: string]: string } = {
  'Produce': 'bg-green-50 border-green-200',
  'Meat': 'bg-red-50 border-red-200',
  'Seafood': 'bg-blue-50 border-blue-200',
  'Dairy': 'bg-yellow-50 border-yellow-200',
  'Bakery': 'bg-amber-50 border-amber-200',
  'Pantry': 'bg-gray-50 border-gray-200',
  'Beverages': 'bg-purple-50 border-purple-200',
  'Condiments': 'bg-orange-50 border-orange-200',
  'Other': 'bg-gray-50 border-gray-200'
}

export const ShoppingListGenerator: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [currentList, setCurrentList] = useState<ShoppingList | null>(null)
  const [savedLists, setSavedLists] = useState<any[]>([])
  const [dateRange, setDateRange] = useState({
    start: format(new Date(), 'yyyy-MM-dd'),
    end: format(addDays(new Date(), 7), 'yyyy-MM-dd')
  })
  const [servingsMultiplier, setServingsMultiplier] = useState(1.0)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [showCustomItemForm, setShowCustomItemForm] = useState(false)
  const [customItem, setCustomItem] = useState({ name: '', quantity: '', unit: '' })
  const [editMode, setEditMode] = useState(false)

  useEffect(() => {
    loadSavedLists()
  }, [])

  const loadSavedLists = async () => {
    try {
      const lists = await apiClient.get('/api/shopping/lists')
      setSavedLists(Array.isArray(lists) ? lists : [])
      // Auto-load the first saved list
      if (Array.isArray(lists) && lists.length > 0) {
        const active = lists[0]
        let items = active.items
        if (typeof items === 'string') {
          try { items = JSON.parse(items) } catch { items = [] }
        }
        if (Array.isArray(items)) {
          setCurrentList({
            list_id: active.id,
            date_range: { start: active.created_at, end: active.updated_at },
            total_items: items.length,
            meals_included: [],
            sections: items.map ? items : []
          })
        }
      }
    } catch (error) {
      console.error('Failed to load saved lists:', error)
    }
  }

  const generateList = async () => {
    setGenerating(true)
    try {
      const response = await apiClient.get('/api/shopping/generate', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          servings_multiplier: servingsMultiplier
        }
      })
      
      setCurrentList(response)
      // Expand all sections by default
      const sections = new Set(response.sections.map((s: any) => s.section))
      setExpandedSections(sections)
      
      toast.success(`Generated shopping list with ${response.total_items} items`)
    } catch (error) {
      toast.error('Failed to generate shopping list')
    } finally {
      setGenerating(false)
    }
  }

  const loadList = async (listId: string) => {
    setLoading(true)
    try {
      const response = await apiClient.get(`/api/shopping/list/${listId}`)
      setCurrentList(response)
      const sections = new Set(response.sections.map((s: any) => s.section))
      setExpandedSections(sections)
    } catch (error) {
      toast.error('Failed to load shopping list')
    } finally {
      setLoading(false)
    }
  }

  const toggleItem = async (itemId: string, sectionIndex: number, itemIndex: number) => {
    if (!currentList) return
    
    try {
      const response = await apiClient.put(`/api/shopping/item/${itemId}/toggle`)
      
      // Update local state
      const newList = { ...currentList }
      newList.sections[sectionIndex].items[itemIndex].checked = response.checked
      setCurrentList(newList)
    } catch (error) {
      toast.error('Failed to update item')
    }
  }

  const addCustomItem = async () => {
    if (!currentList || !customItem.name) return
    
    try {
      const response = await apiClient.post('/api/shopping/add-custom-item', {
        list_id: currentList.id,
        name: customItem.name,
        quantity: customItem.quantity,
        unit: customItem.unit
      })
      
      // Add to appropriate section
      const newList = { ...currentList }
      const sectionIndex = newList.sections.findIndex(s => s.section === response.section)
      if (sectionIndex >= 0) {
        newList.sections[sectionIndex].items.push({
          id: response.id,
          name: response.name,
          quantity: response.quantity,
          unit: response.unit,
          section: response.section,
          recipes: ['Custom'],
          checked: false
        })
      } else {
        // Create new section
        newList.sections.push({
          section: response.section,
          items: [{
            id: response.id,
            name: response.name,
            quantity: response.quantity,
            unit: response.unit,
            section: response.section,
            recipes: ['Custom'],
            checked: false
          }]
        })
      }
      
      setCurrentList(newList)
      setCustomItem({ name: '', quantity: '', unit: '' })
      setShowCustomItemForm(false)
      toast.success('Added custom item')
    } catch (error) {
      toast.error('Failed to add item')
    }
  }

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const getCheckedCount = () => {
    if (!currentList) return { checked: 0, total: 0 }
    let checked = 0
    let total = 0
    currentList.sections.forEach(section => {
      section.items.forEach(item => {
        total++
        if (item.checked) checked++
      })
    })
    return { checked, total }
  }

  const printList = () => {
    window.print()
  }

  const exportList = () => {
    if (!currentList) return
    
    let text = `Shopping List\n${format(new Date(currentList.date_range.start + 'T12:00:00'), 'MMM d')} - ${format(new Date(currentList.date_range.end + 'T12:00:00'), 'MMM d, yyyy')}\n\n`
    
    currentList.sections.forEach(section => {
      text += `${section.section}\n`
      text += '─'.repeat(20) + '\n'
      section.items.forEach(item => {
        const check = item.checked ? '☑' : '☐'
        const qty = item.quantity ? `${item.quantity} ${item.unit}`.trim() : ''
        text += `${check} ${item.name} ${qty}\n`
      })
      text += '\n'
    })
    
    // Create download link
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `shopping-list-${format(new Date(), 'yyyy-MM-dd')}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const { checked, total } = getCheckedCount()
  const progress = total > 0 ? (checked / total) * 100 : 0

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <ShoppingCart className="w-7 h-7 mr-3" />
              Shopping List Generator
            </h1>
            <p className="mt-1 opacity-90">Create shopping lists from your meal plans</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={printList}
              disabled={!currentList}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 disabled:opacity-50"
              title="Print List"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button
              onClick={exportList}
              disabled={!currentList}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 disabled:opacity-50"
              title="Export List"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Generator Controls */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Generate New List</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Servings Multiplier
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setServingsMultiplier(Math.max(0.5, servingsMultiplier - 0.5))}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                -
              </button>
              <input
                type="number"
                value={servingsMultiplier}
                onChange={(e) => setServingsMultiplier(parseFloat(e.target.value) || 1)}
                className="w-20 text-center px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                step="0.5"
                min="0.5"
                max="10"
              />
              <button
                onClick={() => setServingsMultiplier(Math.min(10, servingsMultiplier + 0.5))}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                +
              </button>
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={generateList}
              disabled={generating}
              className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4" />
                  Generate List
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Current List */}
      {currentList && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Shopping List</h2>
              <p className="text-sm text-gray-600">
                {format(new Date(currentList.date_range.start + 'T12:00:00'), 'MMM d')} - {format(new Date(currentList.date_range.end + 'T12:00:00'), 'MMM d, yyyy')}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {checked} of {total} items checked
              </div>
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <button
                onClick={() => setShowCustomItemForm(!showCustomItemForm)}
                className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                title="Add Custom Item"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Custom Item Form */}
          {showCustomItemForm && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Item name"
                  value={customItem.name}
                  onChange={(e) => setCustomItem({ ...customItem, name: e.target.value })}
                  className="flex-1 px-3 py-2 border rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Quantity"
                  value={customItem.quantity}
                  onChange={(e) => setCustomItem({ ...customItem, quantity: e.target.value })}
                  className="w-24 px-3 py-2 border rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Unit"
                  value={customItem.unit}
                  onChange={(e) => setCustomItem({ ...customItem, unit: e.target.value })}
                  className="w-24 px-3 py-2 border rounded-lg"
                />
                <button
                  onClick={addCustomItem}
                  disabled={!customItem.name}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowCustomItemForm(false)
                    setCustomItem({ name: '', quantity: '', unit: '' })
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Sections */}
          <div className="space-y-2">
            {currentList.sections.map((section, sectionIndex) => (
              <div 
                key={section.section}
                className={`border rounded-lg ${sectionColors[section.section] || 'bg-gray-50 border-gray-200'}`}
              >
                <button
                  onClick={() => toggleSection(section.section)}
                  className="w-full flex items-center justify-between p-3 hover:bg-black/5"
                >
                  <div className="flex items-center gap-2">
                    {sectionIcons[section.section] || sectionIcons['Other']}
                    <span className="font-medium">{section.section}</span>
                    <span className="text-sm text-gray-600">({section.items.length} items)</span>
                  </div>
                  {expandedSections.has(section.section) ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                
                {expandedSections.has(section.section) && (
                  <div className="p-3 pt-0">
                    <div className="space-y-1">
                      {section.items.map((item, itemIndex) => (
                        <div 
                          key={item.id || itemIndex}
                          className={`flex items-center gap-3 p-2 rounded-lg hover:bg-black/5 ${
                            item.checked ? 'opacity-50' : ''
                          }`}
                        >
                          <button
                            onClick={() => item.id && toggleItem(item.id, sectionIndex, itemIndex)}
                            className="flex-shrink-0"
                          >
                            {item.checked ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <Circle className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                          <div className="flex-1">
                            <div className={`font-medium ${item.checked ? 'line-through' : ''}`}>
                              {item.name}
                            </div>
                            {(item.quantity || item.unit) && (
                              <div className="text-sm text-gray-600">
                                {item.quantity} {item.unit}
                              </div>
                            )}
                            {item.recipes && item.recipes.length > 0 && item.recipes[0] !== 'Custom' && (
                              <div className="text-xs text-gray-500">
                                For: {item.recipes.join(', ')}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setCustomItem({ name: item.name, quantity: String(item.quantity || ''), unit: item.unit || '' })
                              setShowCustomItemForm(true)
                              // Remove old item
                              if (currentList) {
                                const updated = { ...currentList, sections: currentList.sections.map((s, si) =>
                                  si === sectionIndex ? { ...s, items: s.items.filter((_, ii) => ii !== itemIndex) } : s
                                )}
                                setCurrentList(updated)
                                apiClient.saveShoppingList(updated.sections).catch(() => {})
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm px-2"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (currentList) {
                                const updated = { ...currentList, sections: currentList.sections.map((s, si) =>
                                  si === sectionIndex ? { ...s, items: s.items.filter((_, ii) => ii !== itemIndex) } : s
                                )}
                                setCurrentList(updated)
                                apiClient.saveShoppingList(updated.sections).catch(() => {})
                              }
                            }}
                            className="text-red-500 hover:text-red-700 text-sm px-1"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Saved Lists */}
      {savedLists.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Recent Lists</h2>
          <div className="space-y-2">
            {savedLists.slice(0, 5).map(list => (
              <button
                key={list.id}
                onClick={() => loadList(list.id)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <div className="text-left">
                    <div className="font-medium">
                      {format(new Date(list.start_date + 'T12:00:00'), 'MMM d')} - {format(new Date(list.end_date + 'T12:00:00'), 'MMM d')}
                    </div>
                    <div className="text-sm text-gray-600">
                      {list.total_items} items • {list.checked_items} checked
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {format(new Date(list.created_at), 'MMM d, h:mm a')}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}